/*
 * Purpose: Known connectors & APIs AutoBlocks can recommend or scaffold
 * Last modified: 2026-06-25 | Agent
 * Completeness: 80
 */
export type ConnectorAuth = 'oauth2' | 'api_key' | 'webhook' | 'none';

export interface ConnectorSpec {
  id: string;
  name: string;
  category: string;
  authType: ConnectorAuth;
  docsUrl: string;
  purpose: string;
  keywords: string[];
  setupSteps: string[];
  /** Simulated dry-run response shape */
  dryRunResponse?: Record<string, unknown>;
}

export interface ApiSpec {
  id: string;
  name: string;
  baseUrl: string;
  docsUrl: string;
  purpose: string;
  keywords: string[];
  methods: string[];
  authHeader?: string;
  dryRunResponse?: Record<string, unknown>;
}

export const CONNECTOR_CATALOG: ConnectorSpec[] = [
  {
    id: 'gmail_inbound',
    name: 'Gmail inbound (AutoBlocks webhook)',
    category: 'email',
    authType: 'webhook',
    docsUrl: 'https://developers.google.com/gmail/api',
    purpose: 'Receive parsed email JSON via local webhook receiver — live path from dry run.',
    keywords: ['gmail', 'email', 'inbox', 'inbound', 'webhook'],
    setupSteps: [
      'Run npm run dev (starts webhook API on port 3001).',
      'Create inbox in AutoBlocks — copy POST URL.',
      'Gmail filter + Make/Zapier: parse email → POST JSON to inbox URL.',
    ],
    dryRunResponse: {
      from: 'jobs-noreply@linkedin.com',
      subject: 'New jobs',
      roles: [{ company: 'Acme', title: 'Engineer' }],
    },
  },
  {
    id: 'gmail_inbound_legacy',
    name: 'Gmail (inbound via parser)',
    category: 'email',
    authType: 'oauth2',
    docsUrl: 'https://developers.google.com/gmail/api',
    purpose: 'Receive job alerts, receipts, or calendar invites from email.',
    keywords: ['email', 'gmail', 'inbox', 'linkedin jobs', 'receipt', 'alert'],
    setupSteps: [
      'Create Gmail filter for sender/subject (e.g. linkedin.com + "job alert").',
      'Use Make.com, Zapier, or n8n to parse email → JSON webhook.',
      'Point webhook to your AutoBlocks inbound URL (or manual paste for MVP).',
    ],
    dryRunResponse: {
      from: 'jobs-noreply@linkedin.com',
      subject: 'New jobs matching Software Engineer',
      body: 'Acme Corp — Senior Engineer — Remote',
      roles: [{ company: 'Acme Corp', title: 'Senior Engineer', url: 'https://linkedin.com/jobs/123' }],
    },
  },
  {
    id: 'linkedin_jobs',
    name: 'LinkedIn Jobs',
    category: 'jobs',
    authType: 'api_key',
    docsUrl: 'https://learn.microsoft.com/en-us/linkedin/',
    purpose: 'Search roles, apply, or message hiring contacts.',
    keywords: ['linkedin', 'job', 'apply', 'hiring', 'career'],
    setupSteps: [
      'Create app at linkedin.com/developers — request Sign In with LinkedIn (OIDC).',
      'Redirect URL: http://localhost:3000/oauth/linkedin/callback',
      'Save LINKEDIN_CLIENT_ID + LINKEDIN_CLIENT_SECRET in Secrets, then Connect LinkedIn.',
      'Token stored as LINKEDIN_TOKEN for HTTP blocks (assisted apply; not Talent partner APIs).',
    ],
    dryRunResponse: { applied: true, jobId: 'sim-job-001', channel: 'linkedin' },
  },
  {
    id: 'openai_completion',
    name: 'OpenAI (resume / cover letter)',
    category: 'ai',
    authType: 'api_key',
    docsUrl: 'https://platform.openai.com/docs/api-reference',
    purpose: 'Generate tailored resume bullets and cover letters from facts.',
    keywords: ['resume', 'cover letter', 'tailor', 'ai', 'gpt', 'write'],
    setupSteps: [
      'Add OPENAI_API_KEY to Secrets.',
      'HTTP POST to https://api.openai.com/v1/chat/completions with role facts only.',
    ],
    dryRunResponse: {
      choices: [{ message: { content: '[Dry run] Tailored resume section for Acme Corp — Senior Engineer.' } }],
    },
  },
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    category: 'calendar',
    authType: 'oauth2',
    docsUrl: 'https://developers.google.com/calendar',
    purpose: 'Reminders before appointments or follow-up dates.',
    keywords: ['calendar', 'appointment', 'reminder', 'booking', 'schedule'],
    setupSteps: [
      'OAuth connect Google Calendar.',
      'Webhook on new booking or poll for events 24h ahead.',
    ],
    dryRunResponse: {
      summary: 'Dental checkup',
      start: new Date(Date.now() + 86400000).toISOString(),
    },
  },
  {
    id: 'slack_notify',
    name: 'Slack Incoming Webhook',
    category: 'notifications',
    authType: 'webhook',
    docsUrl: 'https://api.slack.com/messaging/webhooks',
    purpose: 'Notify a channel when workflows complete.',
    keywords: ['slack', 'notify', 'alert', 'team'],
    setupSteps: ['Create Slack incoming webhook URL.', 'Store as SLACK_WEBHOOK in Secrets.'],
    dryRunResponse: { ok: true },
  },
];

export const API_CATALOG: ApiSpec[] = [
  {
    id: 'openai_chat',
    name: 'OpenAI Chat Completions',
    baseUrl: 'https://api.openai.com/v1/chat/completions',
    docsUrl: 'https://platform.openai.com/docs/api-reference/chat',
    purpose: 'AI text generation for resumes, cover letters, summaries.',
    keywords: ['ai', 'gpt', 'resume', 'cover', 'write', 'tailor'],
    methods: ['POST'],
    authHeader: 'Authorization: Bearer $OPENAI_API_KEY',
    dryRunResponse: {
      choices: [{ message: { content: 'Simulated AI output for dry run.' } }],
    },
  },
  {
    id: 'webhook_site',
    name: 'Webhook.site (test receiver)',
    baseUrl: 'https://webhook.site',
    docsUrl: 'https://webhook.site/',
    purpose: 'Test inbound/outbound webhooks during dry runs.',
    keywords: ['webhook', 'test', 'debug'],
    methods: ['POST'],
    dryRunResponse: { received: true },
  },
];

export function findConnectorsForText(text: string): ConnectorSpec[] {
  const lower = text.toLowerCase();
  return CONNECTOR_CATALOG.filter((c) =>
    c.keywords.some((k) => lower.includes(k))
  );
}

export function findApisForText(text: string): ApiSpec[] {
  const lower = text.toLowerCase();
  return API_CATALOG.filter((a) => a.keywords.some((k) => lower.includes(k)));
}
